import { cloneElement, useState } from 'react';

import styled from 'styled-components';

import { Badge, BadgeVariant } from '@components/badge';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { Checkbox } from '@components/checkbox';
import { FileDropzone } from '@components/file-dropzone';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconDropdown } from '@components/icons/dropdown';
import { IconDropRight } from '@components/icons/dropright';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { Modal, hideModal, useModal } from '@components/modal';
import { RadioGroup } from '@components/radio-group';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { isRequestError } from '@utils/requests';

import type {
  EndpointMethod,
  IntegrationEndpointVariable,
  OpenAPIEndpointCreateItem,
  OpenAPIParseResult,
} from '../integrations-types';
import { TYPE_ICON_MAP } from '../styles';

const getVariableIcon = (type: string) => {
  let icon;
  if (type in TYPE_ICON_MAP) {
    icon = TYPE_ICON_MAP[type as keyof typeof TYPE_ICON_MAP];
  } else {
    const typeMapping: Record<string, keyof typeof TYPE_ICON_MAP> = {
      string: 'text',
      integer: 'number',
      object: 'array',
    };
    const mappedType = typeMapping[type];
    icon = mappedType ? TYPE_ICON_MAP[mappedType] : TYPE_ICON_MAP.text;
  }
  return cloneElement(icon, { size: 12 });
};

const extractUploadError = (error: unknown): string | null => {
  if (!isRequestError(error)) return null;
  const data = error.response?.data;
  if (!data || typeof data !== 'object') return null;

  // DRF field validation errors: { file: ["error message"] }
  if ('file' in data && Array.isArray(data.file) && data.file.length > 0) {
    return data.file[0];
  }

  // DRF non-field errors: { non_field_errors: ["error message"] }
  if ('non_field_errors' in data && Array.isArray(data.non_field_errors)) {
    return data.non_field_errors[0];
  }

  // Generic detail: { detail: "error message" }
  if ('detail' in data && typeof data.detail === 'string') {
    return data.detail;
  }

  // Parse result errors: { errors: ["error message"] }
  if ('errors' in data && Array.isArray(data.errors) && data.errors.length > 0) {
    return data.errors[0];
  }

  return null;
};

export type OpenAPIImportModalProps = {
  currentBaseUrl?: string;
  onParse: (file: File) => Promise<OpenAPIParseResult>;
  onCreate: (params: {
    endpoints: OpenAPIEndpointCreateItem[];
    updateBaseUrl: boolean;
    baseUrl?: string | null;
  }) => Promise<{ createdCount: number }>;
};

export const useOpenAPIImportModal = () => {
  return useModal(OpenAPIImportModal, { variant: 'LARGE' });
};

type Step = 'upload' | 'preview';

const OpenAPIImportModal = ({ currentBaseUrl, onParse, onCreate }: OpenAPIImportModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [step, setStep] = useState<Step>('upload');
  const [isLoading, setIsLoading] = useState(false);
  const [parseResult, setParseResult] = useState<OpenAPIParseResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleFileSelect = async (file: File) => {
    setIsLoading(true);
    setParseError(null);
    try {
      const result = await onParse(file);
      setParseResult(result);
      if (result.isSuccess && result.endpoints.length > 0) {
        setStep('preview');
      } else if (!result.isSuccess) {
        setParseError(result.errors[0] || t('parseError'));
      } else {
        setParseError(t('noEndpoints'));
      }
    } catch (error) {
      setParseError(extractUploadError(error) ?? t('parseError'));
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'preview' && parseResult) {
    return (
      <PreviewStep
        isLoading={isLoading}
        parseResult={parseResult}
        currentBaseUrl={currentBaseUrl}
        onCreate={onCreate}
        onBack={() => {
          setStep('upload');
          setParseResult(null);
          setParseError(null);
        }}
      />
    );
  }
  return (
    <UploadStep
      onFileSelect={handleFileSelect}
      error={parseError}
      warnings={parseResult?.warnings}
      isLoading={isLoading}
    />
  );
};

type UploadStepProps = {
  onFileSelect: (file: File) => void;
  error: string | null;
  warnings?: string[];
  isLoading: boolean;
};

const UploadStep = ({ onFileSelect, error, warnings, isLoading }: UploadStepProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Modal isLoading={isLoading}>
      <Modal.Header>
        <Text font="heading1" text={t('title')} />
      </Modal.Header>
      <Modal.Content>
        <Stack space={4}>
          <Text font="regular14" color="textSecondary" text={t('uploadDescription')} />
          <FileDropzone
            fileTypes={['json', 'yaml', 'yml']}
            onChange={(file) => {
              if (file) {
                onFileSelect(file);
              }
            }}
          />
          {error && (
            <Card hasBorder={false} variant="red" space={3}>
              <Stack orientation="horizontal" align="center" space={2}>
                <IconFormIndicatorError size={16} color="error" />
                <Text font="regular14" color="error" text={error} />
              </Stack>
            </Card>
          )}
          {warnings && warnings.length > 0 && (
            <Card hasBorder={false} variant="orange" space={3}>
              <Stack orientation="horizontal" align="center" space={2}>
                <IconFormIndicatorWarning size={16} color="elementOrange100" />
                <Text font="regular14" color="elementOrange100" text={warnings.join(', ')} />
              </Stack>
            </Card>
          )}
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="flex-end">
          <Button variant="secondary" text={t('core.cancel')} onClick={hideModal} />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

type PreviewStepProps = {
  parseResult: OpenAPIParseResult;
  currentBaseUrl?: string;
  onCreate: OpenAPIImportModalProps['onCreate'];
  onBack: () => void;
  isLoading: boolean;
};

const PreviewStep = ({
  parseResult,
  currentBaseUrl,
  onCreate,
  onBack,
  isLoading,
}: PreviewStepProps) => {
  const { t } = useScopedTranslate(locale);
  const [selectedEndpoints, setSelectedEndpoints] = useState<Set<number>>(() => {
    return new Set(parseResult.endpoints.map((_, index) => index));
  });
  const [isCreating, setIsCreating] = useState(false);
  const [expandedEndpoints, setExpandedEndpoints] = useState<Set<number>>(new Set());
  const [allExpanded, setAllExpanded] = useState(false);

  const specHasBaseUrl = Boolean(parseResult.baseUrl);
  const integrationMissingBaseUrl = !currentBaseUrl;
  const baseUrlsDiffer = Boolean(currentBaseUrl && parseResult.baseUrl !== currentBaseUrl);
  const shouldShowBaseUrlSection = specHasBaseUrl && (integrationMissingBaseUrl || baseUrlsDiffer);

  const defaultBaseUrlChoice = integrationMissingBaseUrl && specHasBaseUrl ? 'spec' : 'current';
  const [baseUrlChoice, setBaseUrlChoice] = useState<'spec' | 'current'>(defaultBaseUrlChoice);

  const toggleEndpoint = (index: number) => {
    setSelectedEndpoints((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedEndpoints(new Set(parseResult.endpoints.map((_, index) => index)));
  };

  const deselectAll = () => {
    setSelectedEndpoints(new Set());
  };

  const toggleExpand = (index: number) => {
    setExpandedEndpoints((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedEndpoints(new Set());
    } else {
      setExpandedEndpoints(new Set(parseResult.endpoints.map((_, i) => i)));
    }
    setAllExpanded(!allExpanded);
  };

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      const endpoints: OpenAPIEndpointCreateItem[] = parseResult.endpoints
        .map((endpoint, originalIndex) => ({ endpoint, originalIndex }))
        .filter(({ originalIndex }) => selectedEndpoints.has(originalIndex))
        .map(({ endpoint }) => ({
          name: endpoint.name,
          path: endpoint.path,
          method: endpoint.method,
          requestType: endpoint.requestType,
          variables: endpoint.variables as IntegrationEndpointVariable[],
          responseValues: endpoint.responseValues,
        }));

      await onCreate({
        endpoints,
        updateBaseUrl: baseUrlChoice === 'spec',
        baseUrl: parseResult.baseUrl,
      });

      hideModal();
    } catch (error) {
    } finally {
      setIsCreating(false);
    }
  };

  const allSelected = selectedEndpoints.size === parseResult.endpoints.length;
  const noneSelected = selectedEndpoints.size === 0;

  return (
    <Modal isLoading={isLoading}>
      <Modal.Header>
        <Text font="heading1" text={t('title')} />
        {parseResult.title && (
          <TitleRow>
            <Text font="semibold14" text={t('apiTitle')} />
            <Text font="regular14" text={parseResult.title} color="textSecondary" />
          </TitleRow>
        )}
      </Modal.Header>
      <Modal.Content>
        <Stack space={4}>
          {shouldShowBaseUrlSection && (
            <Card hasBorder variant="gray" spaceX={4} spaceY={3}>
              <Stack space={3}>
                <Text font="semibold14" text={t('baseUrlRequired')} />
                <RadioGroup
                  label={t('baseUrl')}
                  hideLabel
                  value={baseUrlChoice}
                  onChange={(v) => setBaseUrlChoice(v as 'spec' | 'current')}
                  options={[
                    {
                      value: 'spec',
                      label: t('specBaseUrl'),
                      helpText: parseResult.baseUrl || t('notSet'),
                    },
                    {
                      value: 'current',
                      label: t('currentBaseUrl'),
                      helpText: currentBaseUrl || t('notSet'),
                    },
                  ]}
                />
              </Stack>
            </Card>
          )}

          <EndpointListContainer>
            <StyledCard spaceX={4} spaceY={2} variant="gray" hasBorder={false} $isHeader>
              <Stack orientation="horizontal" justify="space-between">
                <Stack space={4} orientation="horizontal" align="center">
                  <Checkbox
                    label=""
                    labelVisible={false}
                    value={allSelected ? true : noneSelected ? false : 'mixed'}
                    onChange={() => {
                      if (allSelected) {
                        deselectAll();
                      } else {
                        selectAll();
                      }
                    }}
                    aria-label={t('selectAllAria')}
                  />

                  <Text font="semibold14" color="textSecondary" text={t('selectDeselectAll')} />
                </Stack>
                <Stack orientation="horizontal" space={4} align="center">
                  <Text
                    font="regular14"
                    color="textSecondary"
                    text={t('endpointsFound', { count: parseResult.endpoints.length })}
                  />
                  <ExpandAllButton
                    onClick={toggleExpandAll}
                    aria-label={allExpanded ? t('collapseAllAria') : t('expandAllAria')}
                  >
                    {allExpanded ? <IconDropdown /> : <IconDropRight />}
                  </ExpandAllButton>
                </Stack>
              </Stack>
            </StyledCard>
            {parseResult.endpoints.map((endpoint, index) => {
              const isSelected = selectedEndpoints.has(index);
              const isExpanded = expandedEndpoints.has(index);
              const isLast = index === parseResult.endpoints.length - 1;

              return (
                <StyledCard
                  spaceX={4}
                  spaceY={2}
                  key={index}
                  variant="gray"
                  interactive
                  hasBorder={false}
                  onClick={() => toggleExpand(index)}
                  $isLast={isLast}
                >
                  <Stack space={2}>
                    <Stack key={index} orientation="horizontal" justify="space-between">
                      <Stack space={4} orientation="horizontal" align="center">
                        <Stack align="center" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            label=""
                            labelVisible={false}
                            value={isSelected}
                            onChange={() => toggleEndpoint(index)}
                            aria-label={t('selectEndpointAria', { name: endpoint.name })}
                          />
                        </Stack>
                        <MethodBadgeColumn>
                          <MethodBadge method={endpoint.method} />
                        </MethodBadgeColumn>
                        <div style={{ maxWidth: pxToRem(360) }}>
                          <Text font="semibold14" text={endpoint.name} ellipsis />
                          <Text
                            font="regular12"
                            color="textSecondary"
                            text={endpoint.path}
                            ellipsis
                          />
                        </div>
                      </Stack>
                      <Stack space={4} orientation="horizontal" align="center">
                        <Badge>
                          {endpoint.variables.length} {t('variables')}
                        </Badge>
                        <ExpandIcon>{isExpanded ? <IconDropdown /> : <IconDropRight />}</ExpandIcon>
                      </Stack>
                    </Stack>
                    {isExpanded && (
                      <EndpointDetails>
                        {endpoint.description && (
                          <Text
                            font="regular14"
                            color="textSecondary"
                            text={endpoint.description}
                          />
                        )}
                        {endpoint.variables.length > 0 ? (
                          <VariableList>
                            {endpoint.variables.map((variable, vIndex) => (
                              <Badge key={vIndex} icon={getVariableIcon(variable.type)}>
                                {variable.name}
                              </Badge>
                            ))}
                          </VariableList>
                        ) : (
                          <Text font="regular14" color="textSecondary" text={t('noVariables')} />
                        )}
                      </EndpointDetails>
                    )}
                  </Stack>
                </StyledCard>
              );
            })}
          </EndpointListContainer>
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="space-between">
          <Button
            variant="secondary"
            iconLeft={<IconArrowLeft />}
            text={t('core.back')}
            onClick={onBack}
          />
          <Stack orientation="horizontal" space={2}>
            <Button variant="secondary" text={t('core.cancel')} onClick={hideModal} />
            <Button
              variant="primary"
              text={
                isCreating
                  ? t('creating')
                  : `${t('create')} \u2022 ${selectedEndpoints.size} ${t('endpointCount')}`
              }
              onClick={handleCreate}
              isDisabled={noneSelected || isCreating}
            />
          </Stack>
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const ENDPOINT_LIST_MAX_HEIGHT = 400;
const CARD_RADIUS = 8;
const ENDPOINT_ROW_PADDING = 12;
const ENDPOINT_DETAILS_PADDING = 44;
const EXPAND_BUTTON_SIZE = 24;

const METHOD_COLORS: Record<EndpointMethod, BadgeVariant> = {
  GET: 'green',
  POST: 'blue',
  PUT: 'orange',
  PATCH: 'orange',
  DELETE: 'red',
};

const METHOD_BADGE_COLUMN_WIDTH = 56;

const MethodBadge = ({ method }: { method: EndpointMethod }) => {
  const color = METHOD_COLORS[method] || METHOD_COLORS.GET;
  return <Badge variant={color} text={method} />;
};

const MethodBadgeColumn = styled(Stack).attrs({
  align: 'center',
  justify: 'center',
})({
  width: pxToRem(METHOD_BADGE_COLUMN_WIDTH),
  flexShrink: 0,
});

const StyledCard = styled(Card)<{ $isHeader?: boolean; $isLast?: boolean }>(
  ({ $isHeader = false, $isLast = false }) => ({
    borderRadius: 'unset',
    cursor: 'pointer',
    borderBottom: $isLast ? 'none' : `1px solid ${theme.color.border10}`,

    ...($isHeader && {
      cursor: 'default',
    }),
  })
);

const TitleRow = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 2,
})({
  marginBottom: pxToRem(8),
});

const EndpointListContainer = styled.div({
  border: `1px solid ${theme.color.border10}`,
  borderRadius: pxToRem(CARD_RADIUS),
  maxHeight: pxToRem(ENDPOINT_LIST_MAX_HEIGHT),
  overflow: 'auto',
});

const EndpointDetails = styled(Stack).attrs({
  orientation: 'vertical',
  space: 2,
})({
  padding: `0 ${pxToRem(ENDPOINT_ROW_PADDING)} ${pxToRem(ENDPOINT_ROW_PADDING)} ${pxToRem(
    ENDPOINT_DETAILS_PADDING
  )}`,
});

const VariableList = styled(Stack).attrs({
  orientation: 'horizontal',
  wrap: true,
  space: 2,
})({});

const ExpandAllButton = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: pxToRem(EXPAND_BUTTON_SIZE),
  height: pxToRem(EXPAND_BUTTON_SIZE),
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  borderRadius: pxToRem(4),
  color: theme.color.textSecondary,
  '&:hover': {
    background: theme.color.uiTrans10,
  },
});

const ExpandIcon = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: pxToRem(EXPAND_BUTTON_SIZE),
  height: pxToRem(EXPAND_BUTTON_SIZE),
  color: theme.color.textSecondary,
  flexShrink: 0,
});

const locale = {
  en: {
    title: 'Import from OpenAPI',
    uploadDescription:
      'Upload an OpenAPI specification file (JSON or YAML) to automatically create endpoints. Supports OpenAPI 2.0 (Swagger) and 3.x specifications.',
    parseError: 'Failed to parse the OpenAPI specification. Please check the file format.',
    noEndpoints: 'No endpoints found in the specification.',
    apiTitle: 'API:',
    baseUrlRequired: 'Base URL required',
    baseUrl: 'Base URL',
    currentBaseUrl: 'Current base URL',
    specBaseUrl: 'Base URL from spec',
    notSet: 'Not set',
    endpointsFound: '{count} endpoint(s) found',
    selectDeselectAll: 'Select/Deselect all',
    selectAllAria: 'Select all endpoints',
    expandAllAria: 'Expand all endpoints',
    collapseAllAria: 'Collapse all endpoints',
    variables: 'Variables',
    noVariables: 'No variables',
    create: 'Create',
    endpointCount: 'endpoint(s)',
    creating: 'Creating...',
    selectEndpointAria: 'Select endpoint {name}',
    expandDetailsAria: 'Expand endpoint details',
    collapseDetailsAria: 'Collapse endpoint details',
  },
};
